import React, { forwardRef, HTMLAttributes, PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../../styles/theme';

interface IProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  hoverColor?: string;
  isDisabled?: boolean;
}

const StyledBase = styled.div<{ $size: number }>`
  position: relative;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  font: inherit;
  outline: none;
`;

const InteractiveBase = styled(StyledBase)<{
  $hoverColor?: string;
  $isDisabled?: boolean;
}>`
  color: ${({ $isDisabled }) =>
    $isDisabled ? theme.colors.secondary400 : theme.colors.secondary600};
  ${({ $isDisabled, $hoverColor }) =>
    !$isDisabled &&
    css`
      &:hover {
        border-radius: 4px;
        background-color: rgba(0, 0, 0, 0.1);
        cursor: pointer;
        color: ${theme.colors.mainBlue};
        &[data-open='true'],
        svg {
          fill: ${$hoverColor || theme.colors.mainBlue};
        }
      }
    `}
`;

const IconWrapper = forwardRef<HTMLDivElement, PropsWithChildren<IProps>>(
  (
    {
      children,
      onClick,
      onMouseDown,
      size = 24,
      hoverColor,
      isDisabled,
      ...props
    },
    ref,
  ) => {
    const isInteractive =
      typeof onClick === 'function' || typeof onMouseDown === 'function';

    if (isInteractive) {
      return (
        <InteractiveBase
          {...props}
          ref={ref}
          onClick={onClick}
          onMouseDown={onMouseDown}
          $size={size}
          $hoverColor={hoverColor}
          $isDisabled={isDisabled}
        >
          {children}
        </InteractiveBase>
      );
    }

    return (
      <StyledBase
        {...props}
        ref={ref}
        onClick={onClick}
        onMouseDown={onMouseDown}
        $size={size}
      >
        {children}
      </StyledBase>
    );
  },
);

export default IconWrapper;
